import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { useEffect } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { NuqsAdapter } from 'nuqs/adapters/react'
import { ThemeProvider } from 'next-themes'
import { routeTree } from './routeTree.gen'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { LaunchDarklyProvider } from './contexts/LaunchDarklyProvider'
import { AppErrorBoundary, ErrorFallback } from './components/AppErrorBoundary'
import { queryClient } from './lib/query-client'
import { installStaleChunkReload } from './lib/stale-chunk-reload'
import './styles.css'
import reportWebVitals from './reportWebVitals.ts'

// A tab opened before a deploy can't load the new build's lazy chunks; reload it.
installStaleChunkReload()

// Create a new router instance
const router = createRouter({
  routeTree,
  basepath: import.meta.env.VITE_BASE_PATH || '/',
  context: {
    auth: {
      isAuthenticated: false,
      isLoading: true,
      departmentResolved: false,
      permissions: [],
      permissionsResolved: false,
      user: null,
      token: null,
      login: () => {},
      loginWithProvider: () => {},
      logout: () => {},
      refreshToken: async () => {},
    },
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  // Contain a render crash to the route that threw, so the shell stays usable
  // and the user sees the actual message instead of a blank page. The router
  // clears this on navigation, unlike a hand-rolled boundary around Outlet.
  defaultErrorComponent: ({ error, reset }) => (
    <ErrorFallback error={error} reset={reset} />
  ),
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function InnerApp() {
  const auth = useAuth()

  // Joined to a primitive so a new-but-equal array identity cannot spin this
  // effect (sc-1215).
  const permissionsKey = auth.permissions.join('|')

  // Re-run route guards (beforeLoad) whenever the auth state settles. Without
  // this, a direct URL/cold refresh mounts an admin page while the department
  // and permissions are still resolving (guards skip while unresolved) and
  // nothing re-checks after they resolve - a user without access would keep
  // seeing the page shell. Invalidation makes the matched routes re-evaluate
  // and the guard redirect fire.
  useEffect(() => {
    void router.invalidate()
  }, [
    auth.isLoading,
    auth.isAuthenticated,
    auth.departmentResolved,
    auth.user?.department,
    auth.permissionsResolved,
    permissionsKey,
  ])

  return (
    <LaunchDarklyProvider>
      <RouterProvider router={router} context={{ auth }} />
    </LaunchDarklyProvider>
  )
}

// Render the app
const rootElement = document.getElementById('app')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <AppErrorBoundary>
      <HelmetProvider>
        <NuqsAdapter>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
              <AuthProvider>
                <InnerApp />
                {/* ReactQueryDevtools intentionally not rendered — same call
                    the portal made in app/providers.tsx. To debug queries,
                    render <ReactQueryDevtools initialIsOpen={false} /> here
                    (import from '@tanstack/react-query-devtools'). */}
              </AuthProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </NuqsAdapter>
      </HelmetProvider>
    </AppErrorBoundary>,
  )
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
